import React, { useEffect, useState } from "react";
import { AppShell } from "../components/Nav";
import { getToken } from "../lib/auth";
import { useAuth } from "../context/AuthContext";

const EDITABLE_VALUES = [
  "Depth over breadth", "Kindness above all", "Growth, always",
  "Independence & partnership", "Family-forward", "Curiosity",
  "Craft & mastery", "Spirituality", "Service",
];

export default function Profile() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [occupation, setOccupation] = useState("");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [values, setValues] = useState([]);

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    fetch("/api/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setProfile(data);
          setName(data.name || "");
          setCity(data.city || "");
          setState(data.state || "");
          setOccupation(data.occupation || "");
          setHeadline(data.headline || "");
          setBio(data.bio || "");
          setValues(data.values || []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const toggleValue = (v) =>
    setValues((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );

  const save = async () => {
    setSaving(true); setSaveMsg("");
    const token = getToken();
    try {
      const res = await fetch("/api/me/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, city, state, occupation, headline, bio, values }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      setProfile(data);
      setSaveMsg("Saved.");
      setEditing(false);
    } catch (e) {
      setSaveMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-12 py-10 text-white/60">Loading profile…</div>
      </AppShell>
    );
  }

  if (!profile) {
    return (
      <AppShell>
        <div className="px-6 md:px-12 py-10 text-white/60">Could not load profile.</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14 max-w-3xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
          My profile
        </div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-4xl md:text-5xl leading-tight">
            {profile.name || "Your Profile"}
          </h1>
          {!editing && (
            <button
              onClick={() => { setSaveMsg(""); setEditing(true); }}
              className="rounded-full border border-white/20 px-5 py-2 text-sm hover:border-white/40"
            >
              Edit
            </button>
          )}
        </div>

        {/* Photo / plan badge */}
        <div className="flex items-center gap-5 mb-8">
          <div className="h-20 w-20 rounded-full bg-white/10 grid place-items-center text-3xl font-display">
            {(profile.name || "?")[0]}
          </div>
          <div>
            <div className="font-display text-xl">{profile.name}</div>
            <div className="text-sm text-white/55">{profile.email}</div>
            <div className="mt-1.5">
              <span className="rounded-full border border-[rgba(212,175,55,0.45)] text-[#F2DE94] text-[10px] uppercase tracking-[0.2em] px-2 py-0.5">
                {profile.plan || profile.subscription_plan || "free"}
              </span>
            </div>
          </div>
        </div>

        <div className="glass rounded-3xl p-6 md:p-8 space-y-6">
          {/* Name */}
          <Field label="Name" editing={editing}>
            {editing
              ? <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
              : <span>{profile.name || "—"}</span>}
          </Field>

          {/* Location */}
          <Field label="City / State" editing={editing}>
            {editing
              ? (
                <div className="flex gap-3">
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className={inputCls + " flex-1"} />
                  <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" className={inputCls + " w-24"} />
                </div>
              )
              : <span>{[profile.city, profile.state].filter(Boolean).join(", ") || "—"}</span>}
          </Field>

          {/* Email (read-only) */}
          <Field label="Email">
            <span className="text-white/55">{profile.email || "—"}</span>
          </Field>

          {/* Occupation */}
          <Field label="Occupation" editing={editing}>
            {editing
              ? <input value={occupation} onChange={(e) => setOccupation(e.target.value)} className={inputCls} />
              : <span>{profile.occupation || "—"}</span>}
          </Field>

          {/* Headline */}
          <Field label="Headline" editing={editing}>
            {editing
              ? <input value={headline} onChange={(e) => setHeadline(e.target.value)} className={inputCls} placeholder="A sentence about you" />
              : <span className="italic">{profile.headline || "—"}</span>}
          </Field>

          {/* Bio */}
          <Field label="Bio" editing={editing}>
            {editing
              ? <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className={inputCls + " resize-none"} />
              : <span className="text-white/75">{profile.bio || "—"}</span>}
          </Field>

          {/* Values */}
          <Field label="Values" editing={editing}>
            {editing
              ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  {EDITABLE_VALUES.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => toggleValue(v)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        values.includes(v)
                          ? "border-[rgba(212,175,55,0.6)] bg-[rgba(212,175,55,0.12)] text-[#F2DE94]"
                          : "border-white/15 text-white/55 hover:border-white/35"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )
              : (
                <div className="flex flex-wrap gap-2">
                  {(profile.values || []).length > 0
                    ? profile.values.map((v) => (
                        <span key={v} className="rounded-full border border-[rgba(212,175,55,0.4)] text-[#F2DE94] text-xs px-3 py-1">
                          {v}
                        </span>
                      ))
                    : <span className="text-white/40">No values set</span>}
                </div>
              )}
          </Field>

          {/* Gender / preference (read-only) */}
          <Field label="Gender · Preference">
            <span className="text-white/55 capitalize">
              {[profile.gender, profile.preference].filter(Boolean).join(" · ") || "—"}
            </span>
          </Field>
        </div>

        {/* Save / cancel */}
        {editing && (
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-gradient-gold text-black font-semibold px-6 py-3 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={() => { setEditing(false); setSaveMsg(""); }}
              className="text-sm text-white/55 hover:text-white"
            >
              Cancel
            </button>
            {saveMsg && (
              <span className={`text-sm ${saveMsg === "Saved." ? "text-emerald-400" : "text-red-400"}`}>
                {saveMsg}
              </span>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-white/30";

function Field({ label, editing, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
